import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { fmtShort } from "./calendarUtils";

const EMPTY = { start_date: "", end_date: "", description: "" };

export const ClosuresSection = ({ year }) => {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setList(null); setForm(EMPTY); setEditId(null);
    if (!year) return;
    api.get("/admin/closures", { params: { year } }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [year]);

  const save = async () => {
    if (!form.start_date) return toast.error("Inserisci almeno la data di inizio");
    setBusy(true);
    try {
      if (editId) {
        const { data } = await api.put(`/admin/closures/${editId}`, form);
        setList((l) => l.map((c) => (c.id === editId ? data : c)).sort((a, b) => a.start_date.localeCompare(b.start_date)));
      } else {
        const { data } = await api.post("/admin/closures", form, { params: { year } });
        setList((l) => [...l, data].sort((a, b) => a.start_date.localeCompare(b.start_date)));
      }
      setForm(EMPTY); setEditId(null); toast.success("Salvato");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const prev = list;
    setList((l) => l.filter((c) => c.id !== id));
    try { await api.delete(`/admin/closures/${id}`); toast.success("Chiusura eliminata"); } catch (err) { setList(prev); toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Card className="p-6 bg-card border-border space-y-4" data-testid="closures-section">
      <h3 className="text-xl">Chiusure (anno {year})</h3>
      <p className="text-sm text-muted-foreground">Giorni o periodi in cui la scuola è chiusa. Valgono per tutti i corsi dell'anno e vengono saltati nella generazione del calendario.</p>
      <div className="grid sm:grid-cols-[1fr_1fr_2fr] gap-3">
        <div><Label className="text-xs">Dal</Label><Input data-testid="closure-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 bg-white" /></div>
        <div><Label className="text-xs">Al (vuoto = giorno singolo)</Label><Input data-testid="closure-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 bg-white" /></div>
        <div><Label className="text-xs">Descrizione</Label><Input data-testid="closure-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Es. Vacanze di Natale" className="mt-1 bg-white" /></div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" data-testid="closure-save" onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90">{editId ? <><Save size={14} className="mr-1" /> Salva modifica</> : <><Plus size={14} className="mr-1" /> Aggiungi chiusura</>}</Button>
        {editId && <Button size="sm" variant="ghost" data-testid="closure-cancel-edit" onClick={() => { setEditId(null); setForm(EMPTY); }}><X size={14} className="mr-1" /> Annulla</Button>}
      </div>
      {list === null ? <p className="text-sm text-muted-foreground">Caricamento…</p> : list.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna chiusura inserita.</p> : (
        <ul className="divide-y divide-border border border-border rounded-lg">
          {list.map((c) => (
            <li key={c.id} data-testid={`closure-${c.id}`} className="flex items-center gap-3 p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{c.end_date && c.end_date !== c.start_date ? `${fmtShort(c.start_date)} → ${fmtShort(c.end_date)}` : fmtShort(c.start_date)}</p>
                <p className="text-xs text-muted-foreground truncate">{c.description || "Chiusura"}</p>
              </div>
              <Button size="icon" variant="ghost" data-testid={`closure-edit-${c.id}`} onClick={() => { setEditId(c.id); setForm({ start_date: c.start_date, end_date: c.end_date === c.start_date ? "" : c.end_date, description: c.description }); }}><Pencil size={15} /></Button>
              <Button size="icon" variant="ghost" data-testid={`closure-delete-${c.id}`} onClick={() => remove(c.id)} className="text-destructive hover:text-destructive"><Trash2 size={15} /></Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
